import { create as createFont } from "fontkit";
import type { TextNode } from "@/document/model/nodes";
import type { Paint } from "@/document/model/styles";
import { getCachedTextLayout, type TextLayoutRun } from "@/renderer/text/canvasKitText";
import { googleFontBytesForText } from "@/lib/GoogleFontLoader";

export interface TextSvgPath {
  d: string;
  fill?: Paint;
}

interface FontkitFont {
  unitsPerEm: number;
  glyphForCodePoint(codePoint: number): FontkitGlyph;
  hasGlyphForCodePoint?(codePoint: number): boolean;
  layout?(text: string): FontkitGlyphRun;
}

interface FontkitGlyph {
  path: FontkitPath;
  codePoints?: number[];
}

interface FontkitGlyphPosition {
  xAdvance: number;
  yAdvance: number;
  xOffset: number;
  yOffset: number;
}

interface FontkitGlyphRun {
  glyphs: FontkitGlyph[];
  positions: FontkitGlyphPosition[];
}

interface FontkitPath {
  commands: FontkitPathCommand[];
}

type FontkitPathCommand =
  | { command: "moveTo" | "lineTo"; args: [number, number] }
  | { command: "quadraticCurveTo"; args: [number, number, number, number] }
  | { command: "bezierCurveTo"; args: [number, number, number, number, number, number] }
  | { command: "closePath"; args: [] };

interface PositionedGlyphPath {
  character: string;
  path: FontkitPath;
  x: number;
  y: number;
  index: number;
}

const fontCache = new Map<string, Promise<FontkitFont | undefined>>();

export async function textNodeToSvgPaths(node: TextNode): Promise<TextSvgPath[]> {
  const layout = getCachedTextLayout(node, 1);
  const offsetY = verticalOffset(node.bounds.height, layout.height, node.text.style.verticalAlign);
  const paths: TextSvgPath[] = [];

  for (const run of layout.runs) {
    if (!run.fill || !run.text || !/\S/.test(run.text)) {
      continue;
    }

    const font = await loadRunFont(run);
    if (!font) {
      continue;
    }

    const scale = run.style.fontSize / font.unitsPerEm;
    const outlines = glyphPathsForRun(font, run, scale);
    const baselineX = node.bounds.x + run.x;
    const baselineY = node.bounds.y + offsetY + run.y + run.baseline;

    for (const outline of outlines) {
      const d = glyphPathToPathData(outline, scale, baselineX, baselineY, run);
      if (!d) {
        continue;
      }
      pushPath(paths, { d, fill: run.fill });
    }
  }

  return paths;
}

export async function textNodeToSvgPathData(node: TextNode): Promise<string> {
  return (await textNodeToSvgPaths(node)).map(path => path.d).join(" ");
}

async function loadRunFont(run: TextLayoutRun): Promise<FontkitFont | undefined> {
  const key = `${run.style.fontFamily}:${run.style.fontWeight}:${textCoverageKey(run.text)}`;
  const cached = fontCache.get(key);
  if (cached) {
    return cached;
  }

  const promise = googleFontBytesForText(run.style.fontFamily, run.style.fontWeight, run.text)
    .then(font => font ? createFont(new Uint8Array(font.bytes)) as FontkitFont : undefined)
    .catch(error => {
      console.error("Failed to load font for text vectorization", {
        error,
        fontFamily: run.style.fontFamily,
        fontWeight: run.style.fontWeight,
      });
      return undefined;
    });
  fontCache.set(key, promise);
  return promise;
}

function glyphPathsForRun(font: FontkitFont, run: TextLayoutRun, scale: number): PositionedGlyphPath[] {
  if (run.style.letterSpacing === 0 && font.layout) {
    return shapedGlyphPathsForRun(font, run, scale);
  }

  const paths: PositionedGlyphPath[] = [];
  const characters = [...run.text];
  let x = 0;

  for (let index = 0; index < characters.length; index += 1) {
    const character = characters[index]!;
    const codePoint = character.codePointAt(0);
    const advance = run.advances[index] ?? 0;
    if (codePoint === undefined || !/\S/.test(character)) {
      x += advance;
      continue;
    }
    if (font.hasGlyphForCodePoint && !font.hasGlyphForCodePoint(codePoint)) {
      x += advance;
      continue;
    }

    try {
      paths.push({
        character,
        path: font.glyphForCodePoint(codePoint).path,
        x,
        y: 0,
        index,
      });
    } catch (error) {
      console.error("Failed to extract font glyph path", {
        error,
        text: run.text,
        character,
        fontFamily: run.style.fontFamily,
        fontWeight: run.style.fontWeight,
        glyphIndex: index,
      });
      throw error;
    }

    x += advance;
  }

  return paths;
}

function shapedGlyphPathsForRun(font: FontkitFont, run: TextLayoutRun, scale: number): PositionedGlyphPath[] {
  const glyphRun = font.layout?.(run.text);
  if (!glyphRun) {
    return [];
  }

  const paths: PositionedGlyphPath[] = [];
  let x = 0;
  let y = 0;
  let sourceIndex = 0;

  for (let index = 0; index < glyphRun.glyphs.length; index += 1) {
    const glyph = glyphRun.glyphs[index]!;
    const position = glyphRun.positions[index] ?? { xAdvance: 0, yAdvance: 0, xOffset: 0, yOffset: 0 };
    const glyphText = glyphTextForRunGlyph(run.text, sourceIndex, glyph);
    const character = glyphText || run.text[sourceIndex] || "";

    if (glyphText) {
      sourceIndex += glyphText.length;
    } else if (character) {
      sourceIndex += character.length;
    }

    if (/\S/.test(glyphText || character)) {
      paths.push({
        character,
        path: glyph.path,
        x: (x + position.xOffset) * scale,
        y: (y + position.yOffset) * scale,
        index,
      });
    }

    x += position.xAdvance;
    y += position.yAdvance;
  }

  return paths;
}

function glyphTextForRunGlyph(text: string, start: number, glyph: FontkitGlyph): string {
  const codePoints = glyph.codePoints ?? [];
  if (codePoints.length === 0) {
    return text[start] ?? "";
  }

  const expected = String.fromCodePoint(...codePoints);
  return text.slice(start, start + expected.length) === expected ? expected : text[start] ?? "";
}

function textCoverageKey(text: string): string {
  return textCodePoints(text).join(",");
}

function textCodePoints(text: string): number[] {
  return [...new Set([...text].flatMap(character => {
    if (!/\S/.test(character)) {
      return [];
    }
    const codePoint = character.codePointAt(0);
    return codePoint === undefined ? [] : [codePoint];
  }))].sort((a, b) => a - b);
}

function glyphPathToPathData(glyph: PositionedGlyphPath, scale: number, baselineX: number, baselineY: number, textRun: TextLayoutRun): string {
  try {
    return glyphPathToCommands(glyph.path, scale, baselineX + glyph.x, baselineY - glyph.y).join(" ");
  } catch (error) {
    console.error("Failed to convert font glyph path", {
      error,
      text: textRun.text,
      character: glyph.character,
      fontFamily: textRun.style.fontFamily,
      fontWeight: textRun.style.fontWeight,
      glyphIndex: glyph.index,
    });
    throw error;
  }
}

function glyphPathToCommands(path: FontkitPath, scale: number, baselineX: number, baselineY: number): string[] {
  return path.commands.flatMap(command => {
    switch (command.command) {
      case "moveTo": {
        const point = fontPointToDocument(command.args[0], command.args[1], scale, baselineX, baselineY);
        return [`M ${formatNumber(point.x)} ${formatNumber(point.y)}`];
      }
      case "lineTo": {
        const point = fontPointToDocument(command.args[0], command.args[1], scale, baselineX, baselineY);
        return [`L ${formatNumber(point.x)} ${formatNumber(point.y)}`];
      }
      case "quadraticCurveTo": {
        const control = fontPointToDocument(command.args[0], command.args[1], scale, baselineX, baselineY);
        const end = fontPointToDocument(command.args[2], command.args[3], scale, baselineX, baselineY);
        return [`Q ${formatNumber(control.x)} ${formatNumber(control.y)} ${formatNumber(end.x)} ${formatNumber(end.y)}`];
      }
      case "bezierCurveTo": {
        const c1 = fontPointToDocument(command.args[0], command.args[1], scale, baselineX, baselineY);
        const c2 = fontPointToDocument(command.args[2], command.args[3], scale, baselineX, baselineY);
        const end = fontPointToDocument(command.args[4], command.args[5], scale, baselineX, baselineY);
        return [`C ${formatNumber(c1.x)} ${formatNumber(c1.y)} ${formatNumber(c2.x)} ${formatNumber(c2.y)} ${formatNumber(end.x)} ${formatNumber(end.y)}`];
      }
      case "closePath":
        return ["Z"];
    }
  });
}

function fontPointToDocument(
  x: number,
  y: number,
  scale: number,
  baselineX: number,
  baselineY: number,
): { x: number; y: number } {
  return {
    x: baselineX + x * scale,
    y: baselineY - y * scale,
  };
}

function pushPath(paths: TextSvgPath[], path: TextSvgPath): void {
  const previous = paths.at(-1);
  if (previous && JSON.stringify(previous.fill) === JSON.stringify(path.fill)) {
    previous.d = `${previous.d} ${path.d}`;
    return;
  }
  paths.push(path);
}

function verticalOffset(boxHeight: number, textHeight: number, align: TextNode["text"]["style"]["verticalAlign"]): number {
  if (align === "CENTER") {
    return Math.max(0, (boxHeight - textHeight) / 2);
  }
  if (align === "BOTTOM") {
    return Math.max(0, boxHeight - textHeight);
  }
  return 0;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}
